import { useEffect } from "react";
import Lenis from "lenis";

// Detect CPU/software WebGL (SwiftShader, llvmpipe, Microsoft Basic Render, or no WebGL).
// On those clients a continuous rAF loop forces expensive full-viewport software compositing,
// so we use native scrolling instead. Same signal the 3D hero uses.
function isSoftwareRenderer() {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
    if (!gl) return true;
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const r = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : "";
    return /swiftshader|llvmpipe|software|microsoft basic render/i.test(r);
  } catch (e) {
    return true;
  }
}

// Momentum smooth scrolling wrapper (mandatory premium feel).
// Option C: native scroll for software-renderer / reduced-motion clients; for everyone else
// Lenis stays enabled but its rAF loop runs ON-DEMAND (only while scrolling/settling) and
// goes completely idle afterward, so it never blocks the main thread at rest.
export default function SmoothScroll({ children }) {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // --- Native-scroll path (software GPU or reduced-motion): no Lenis, no rAF loop. ---
    if (reduce || isSoftwareRenderer()) {
      // Shim so existing anchor navigation (window.__lenis.scrollTo) keeps working natively.
      const shim = {
        scrollTo: (target, opts = {}) => {
          const el = typeof target === "string" ? document.querySelector(target) : target;
          if (!el) return;
          const top = el.getBoundingClientRect().top + window.scrollY + (opts.offset || 0);
          window.scrollTo({ top, behavior: "smooth" });
        },
      };
      window.__lenis = shim;
      return () => {
        if (window.__lenis === shim) window.__lenis = null;
      };
    }

    // --- Smooth-scroll path (real GPU): Lenis with an on-demand rAF loop. ---
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.5,
    });

    let rafId = null;
    let idleTimer = null;

    const tick = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(tick);
    };
    const start = () => {
      if (rafId == null) rafId = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
    // Stop the loop once Lenis has fully settled (velocity ~0, no active animation).
    const scheduleStop = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (lenis.isScrolling) scheduleStop();
        else stop();
      }, 200);
    };

    // Every internal Lenis update fires 'scroll'; keep the loop alive and re-arm the idle check.
    lenis.on("scroll", scheduleStop);

    // Any scroll intent kicks the loop so the first frame is captured immediately.
    const kick = () => {
      start();
      scheduleStop();
    };
    const opts = { passive: true };
    window.addEventListener("wheel", kick, opts);
    window.addEventListener("touchstart", kick, opts);
    window.addEventListener("touchmove", kick, opts);
    window.addEventListener("keydown", kick, opts);

    // Programmatic anchor scrolling must also drive the loop until it settles.
    const nativeScrollTo = lenis.scrollTo.bind(lenis);
    lenis.scrollTo = (target, options) => {
      start();
      nativeScrollTo(target, options);
      scheduleStop();
    };

    window.__lenis = lenis;

    return () => {
      lenis.off("scroll", scheduleStop);
      window.removeEventListener("wheel", kick, opts);
      window.removeEventListener("touchstart", kick, opts);
      window.removeEventListener("touchmove", kick, opts);
      window.removeEventListener("keydown", kick, opts);
      if (idleTimer) clearTimeout(idleTimer);
      stop();
      lenis.destroy();
      if (window.__lenis === lenis) window.__lenis = null;
    };
  }, []);

  return children;
}
